import speedyIcon from "../assets/speedy-logo.png";

/** Supported checkout destination type values. */
export const DESTINATION_TYPES = ["home", "office", "box"];

/**
 * When true, office/box use the Speedy iframe locator when the carrier has `officeLocator: "speedy"`.
 * Set to false to use manual address fields for all destination types.
 */
export const SPEEDY_OFFICE_LOCATOR_ENABLED = false;

/**
 * Fallback carrier list used when Sanity has no enabled shipping options.
 * Speedy only — Econt is intentionally omitted for now.
 */
export const FALLBACK_SHIPPING_OPTIONS = [
  {
    name: { bg: "Speedy", en: "Speedy" },
    slug: "speedy",
    logoUrl: speedyIcon,
    enabled: true,
    isDefault: true,
    destinationTypes: [...DESTINATION_TYPES],
    officeLocator: "speedy",
  },
];

/**
 * Resolves a multilanguage Sanity string for the active UI language.
 * @param {{ bg?: string, en?: string }|string|null|undefined} value - Localized value.
 * @param {string} language - Active language code.
 * @returns {string}
 */
export const resolveLocalizedString = (value, language) => {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  return value[language] || value.bg || value.en || "";
};

/**
 * Normalizes a raw Sanity shipping option into a UI-ready shape.
 * @param {Object} option - Raw shipping option from site settings.
 * @returns {Object|null}
 */
export const normalizeShippingOption = (option) => {
  if (!option || !option.slug) {
    return null;
  }

  const destinationTypes =
    Array.isArray(option.destinationTypes) && option.destinationTypes.length > 0
      ? option.destinationTypes.filter((type) => DESTINATION_TYPES.includes(type))
      : [...DESTINATION_TYPES];

  return {
    name: option.name || { bg: option.slug, en: option.slug },
    slug: option.slug,
    logoUrl: option.logo?.asset?.url || null,
    enabled: option.enabled !== false,
    isDefault: Boolean(option.isDefault),
    destinationTypes:
      destinationTypes.length > 0 ? destinationTypes : [...DESTINATION_TYPES],
    officeLocator: option.officeLocator === "speedy" ? "speedy" : "none",
  };
};

/**
 * Returns enabled shipping options from Sanity config, or Speedy fallback.
 * @param {Object|null|undefined} configuration - ConfigContext value.
 * @returns {Array<Object>}
 */
export const getShippingOptions = (configuration) => {
  const fromCms = (configuration?.shippingOptions || [])
    .map(normalizeShippingOption)
    .filter((option) => option && option.enabled);

  return fromCms.length > 0 ? fromCms : FALLBACK_SHIPPING_OPTIONS;
};

/**
 * Picks the default shipping option from a list.
 * @param {Array<Object>} options - Normalized shipping options.
 * @returns {Object|null}
 */
export const getDefaultShippingOption = (options) => {
  if (!options || options.length === 0) {
    return null;
  }
  return options.find((option) => option.isDefault) || options[0];
};

/**
 * Returns destination types supported by a carrier.
 * @param {Object|null|undefined} option - Normalized shipping option.
 * @returns {string[]}
 */
export const getSupportedDestinationTypes = (option) => {
  if (!option?.destinationTypes?.length) {
    return [...DESTINATION_TYPES];
  }
  return option.destinationTypes;
};

/**
 * Whether office/box selection should use the Speedy locator widget.
 * @param {Object|null|undefined} option - Normalized shipping option.
 * @param {string} destinationType - Selected destination type.
 * @returns {boolean}
 */
export const shouldUseSpeedyLocator = (option, destinationType) => {
  if (!SPEEDY_OFFICE_LOCATOR_ENABLED) {
    return false;
  }
  return (
    option?.officeLocator === "speedy" &&
    (destinationType === "office" || destinationType === "box")
  );
};

/**
 * Maps Speedy widget office payload into checkout address fields.
 * Field names vary slightly across widget versions, so access is defensive.
 * @param {Object} office - Payload from Speedy postMessage.
 * @returns {{ address: string, city: string, postalCode: string, officeId: string|number|null, officeName: string }}
 */
export const mapSpeedyOfficeToAddress = (office) => {
  if (!office || typeof office !== "object") {
    return {
      address: "",
      city: "",
      postalCode: "",
      officeId: null,
      officeName: "",
    };
  }

  const officeId = office.id ?? office.officeId ?? office.officeID ?? null;
  const officeName = office.name || office.officeName || office.localName || "";
  const city =
    office.city ||
    office.siteName ||
    office.town ||
    office.address?.city ||
    office.address?.siteName ||
    "";
  const postalCode =
    office.postCode ||
    office.postalCode ||
    office.zip ||
    office.address?.postCode ||
    office.address?.postalCode ||
    "";
  const addressLine =
    office.address?.fullAddress ||
    office.address?.address ||
    office.address ||
    office.fullAddress ||
    office.addressNote ||
    "";

  const address =
    typeof addressLine === "string"
      ? addressLine
      : [officeName, city].filter(Boolean).join(", ");

  return {
    address: address || officeName || String(officeId || ""),
    city: typeof city === "string" ? city : "",
    postalCode: typeof postalCode === "string" ? postalCode : String(postalCode || ""),
    officeId,
    officeName,
  };
};
